import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import type { Salesperson } from "@/hooks/api/use-salespeople";
import { useTrackedLocalStorage } from "./use-tracked-browser-storage";

// Constants
const MAX_RECENT_SALESPEOPLE = 10;

// Minimal type definition for a recent salesperson with only essential fields for storage
export interface RecentSalesperson {
  person_id: string;
  name: string;
  team_name: string | null;
  current_title: string | null;
  timestamp: number;
}

export function useSalespersonSelection() {
  const navigate = useNavigate();
  const params = useParams<{ personId: string }>();
  const location = useLocation();

  // Manage selected salesperson and recent salespeople with automatic organization scoping
  const [selectedPersonId, setSelectedPersonId] =
    useTrackedLocalStorage<string>(
      "salespeople::explorer::selected-salesperson::active",
      "",
    );
  const [recentSalespeople, setRecentSalespeople] = useTrackedLocalStorage<
    RecentSalesperson[]
  >("salespeople::explorer::selected-salesperson::recent", [], {
    migration: {
      from: {
        key: "recentSalespeople",
        storage: "localStorage",
        organizationScoped: true,
      },
      cleanup: true,
    },
  });

  // Get person ID from URL path param only
  const personIdFromPath = params.personId;
  const storedPersonId = selectedPersonId;

  const currentSelectedPersonId = personIdFromPath || storedPersonId;

  // Sort recent salespeople by timestamp (most recent first)
  const sortedRecentSalespeople = useMemo(() => {
    return [...recentSalespeople].sort((a, b) => b.timestamp - a.timestamp);
  }, [recentSalespeople]);

  // Find the selected salesperson in recent salespeople list
  const initialSalesperson = useMemo(() => {
    if (!currentSelectedPersonId || recentSalespeople.length === 0)
      return undefined;
    return recentSalespeople.find(
      (salesperson) => salesperson.person_id === currentSelectedPersonId,
    );
  }, [currentSelectedPersonId, recentSalespeople]);

  // Handle salesperson selection
  const handleSalespersonChange = useCallback(
    (value: string, salesperson: Salesperson | RecentSalesperson | null) => {
      if (value) {
        // Navigate to the salesperson explorer and preserve query (e.g., ?view)
        const target = {
          pathname: `/salespeople/${value}/explorer`,
          search: location.search,
        };
        const isSame =
          location.pathname === target.pathname &&
          location.search === (target.search || "");
        if (!isSame) {
          navigate(target, { replace: true });
        }

        // Also update localStorage for persistence across page loads
        setSelectedPersonId(value);

        // Add to recent salespeople if salesperson object is provided
        if (salesperson) {
          // Extract minimal fields and add with current timestamp (works for both full and recent objects)
          setRecentSalespeople((prev) => {
            const filtered = (prev || []).filter(
              (s) => s.person_id !== salesperson.person_id,
            );
            return [
              {
                person_id: salesperson.person_id,
                name: salesperson.name,
                team_name: salesperson.team_name,
                current_title: salesperson.current_title,
                timestamp: Date.now(),
              },
              ...filtered,
            ].slice(0, MAX_RECENT_SALESPEOPLE);
          });
        }
      } else {
        // Remove from localStorage only
        setSelectedPersonId("");
      }
    },
    [
      navigate,
      setRecentSalespeople,
      setSelectedPersonId,
      location.pathname,
      location.search,
    ],
  );

  // Initialize URL from localStorage if needed
  useEffect(() => {
    // If we have a stored selection but no URL param, update the URL
    if (!personIdFromPath && storedPersonId && selectedPersonId) {
      // For explorer pages, use the URL pattern
      if (location.pathname === "/salespeople/explorer") {
        navigate(`/salespeople/${storedPersonId}/explorer`, { replace: true });
      }
    }
  }, [
    personIdFromPath,
    storedPersonId,
    selectedPersonId,
    navigate,
    location.pathname,
  ]);

  return {
    selectedPersonId: currentSelectedPersonId,
    initialSalesperson,
    handleSalespersonChange,
    recentSalespeople: sortedRecentSalespeople,
  };
}
